/**
 * IGS Analytics for Promarine.
 *
 * Single browser-side analytics layer for Shopify + Umami.
 * No purchase event is emitted here: purchases belong to the orders/paid backend.
 */
(() => {
  'use strict';

  if (window.__IGS_ANALYTICS__) return;

  const SCRIPT_ID = 'igs-analytics';
  const ATTRIBUTION_KEY = 'igs-attribution-v1';
  const ATTRIBUTION_EVENT_KEY = 'igs-attribution-event-v1';
  const DEDUPE_WINDOW_MS = 2000;
  const UMAMI_WAIT_ATTEMPTS = 20;
  const UMAMI_WAIT_MS = 250;
  const ALLOWED_EVENTS = new Set([
    'product-view',
    'add-to-cart',
    'remove-from-cart',
    'cart-view',
    'begin-checkout',
    'whatsapp-click',
    'influencer-attributed',
  ]);
  const ALLOWED_PAYLOAD_KEYS = new Set([
    'product_handle',
    'variant_id',
    'quantity',
    'value',
    'currency',
    'influencer_code',
    'campaign_id',
    'discount_code',
  ]);
  const ATTRIBUTION_KEYS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'influencer_code',
    'campaign_id',
  ];

  const script = document.getElementById(SCRIPT_ID);
  const config = {
    pageType: script?.dataset.pageType || '',
    productHandle: script?.dataset.productHandle || '',
    variantId: script?.dataset.variantId || '',
    valueCents: toNumber(script?.dataset.valueCents),
    currency: sanitizeToken(script?.dataset.currency, 12),
  };
  const recentEvents = new Map();
  let cartSnapshot = new Map();
  let cartCurrency = config.currency;
  let cartTotal = 0;

  function toNumber(value) {
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
  }

  function sanitizeToken(value, maxLength = 120) {
    if (typeof value !== 'string') return '';
    return value
      .trim()
      .replace(/[^\p{L}\p{N}._:@/+ -]/gu, '')
      .slice(0, maxLength);
  }

  function looksLikePii(value) {
    if (typeof value !== 'string') return false;
    const normalized = value.trim();
    const looksLikeEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized);
    const looksLikePhone =
      /^[+\s().-]*\d[\d\s().-]{7,}$/.test(normalized) &&
      /[+\s().-]/.test(normalized);
    return looksLikeEmail || looksLikePhone;
  }

  function safeStorage(storage) {
    try {
      const key = '__igs_test__';
      storage.setItem(key, '1');
      storage.removeItem(key);
      return storage;
    } catch {
      return null;
    }
  }

  const sessionStore = safeStorage(window.sessionStorage);
  const localStore = safeStorage(window.localStorage);

  function readJson(storage, key) {
    if (!storage) return {};
    try {
      const value = JSON.parse(storage.getItem(key) || '{}');
      return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
    } catch {
      return {};
    }
  }

  function writeJson(storage, key, value) {
    if (!storage) return;
    try {
      storage.setItem(key, JSON.stringify(value));
    } catch {
      // Analytics must never break the storefront.
    }
  }

  function attributionFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const first = (...keys) => {
      for (const key of keys) {
        const value = sanitizeToken(params.get(key));
        if (value) return value;
      }
      return '';
    };

    return {
      utm_source: first('utm_source'),
      utm_medium: first('utm_medium'),
      utm_campaign: first('utm_campaign'),
      influencer_code: first('influencer_code', 'influencer', 'code'),
      campaign_id: first('campaign_id', 'campaign'),
    };
  }

  function persistAttribution() {
    const existing = {
      ...readJson(localStore, ATTRIBUTION_KEY),
      ...readJson(sessionStore, ATTRIBUTION_KEY),
    };
    const incoming = attributionFromUrl();
    const merged = {};

    for (const key of ATTRIBUTION_KEYS) {
      const value = sanitizeToken(incoming[key] || existing[key]);
      if (value) merged[key] = value;
    }

    writeJson(sessionStore, ATTRIBUTION_KEY, merged);
    writeJson(localStore, ATTRIBUTION_KEY, merged);
    return merged;
  }

  const attribution = persistAttribution();

  function currentDiscountCode() {
    const params = new URLSearchParams(window.location.search);
    const urlCode = sanitizeToken(params.get('discount_code') || params.get('discount'));
    if (urlCode) return urlCode;

    const pill = document.querySelector('[data-discount-code]');
    return sanitizeToken(pill?.dataset?.discountCode);
  }

  function buildPayload(input = {}) {
    const candidate = {
      ...input,
      influencer_code: input.influencer_code || attribution.influencer_code,
      campaign_id: input.campaign_id || attribution.campaign_id,
      discount_code: input.discount_code || currentDiscountCode(),
    };
    const payload = {};

    for (const [key, rawValue] of Object.entries(candidate)) {
      if (!ALLOWED_PAYLOAD_KEYS.has(key) || rawValue === '' || rawValue == null) continue;

      if (key === 'quantity') {
        const quantity = Math.max(0, Math.round(toNumber(rawValue)));
        if (quantity) payload[key] = quantity;
      } else if (key === 'value') {
        const value = Math.max(0, toNumber(rawValue));
        if (value) payload[key] = Number(value.toFixed(2));
      } else if (key === 'variant_id') {
        const variantId = sanitizeToken(String(rawValue), 32);
        if (/^\d+$/.test(variantId)) payload[key] = variantId;
      } else {
        const value = sanitizeToken(String(rawValue), key === 'currency' ? 12 : 120);
        if (value && !looksLikePii(value)) payload[key] = value;
      }
    }

    return payload;
  }

  function eventFingerprint(name, payload) {
    if (name === 'add-to-cart' || name === 'remove-from-cart') {
      return `${name}|${payload.variant_id || ''}|${payload.quantity || ''}`;
    }
    if (name === 'begin-checkout' || name === 'whatsapp-click' || name === 'cart-view') {
      return name;
    }
    const stablePayload = Object.keys(payload)
      .sort()
      .map((key) => `${key}:${String(payload[key])}`)
      .join('|');
    return `${name}|${stablePayload}`;
  }

  function isDuplicate(name, payload) {
    const now = Date.now();
    const fingerprint = eventFingerprint(name, payload);
    const previous = recentEvents.get(fingerprint);

    for (const [key, timestamp] of recentEvents) {
      if (now - timestamp > DEDUPE_WINDOW_MS * 3) recentEvents.delete(key);
    }

    if (previous && now - previous < DEDUPE_WINDOW_MS) return true;
    recentEvents.set(fingerprint, now);
    return false;
  }

  function sendToUmami(name, payload, attempt = 0) {
    if (window.umami && typeof window.umami.track === 'function') {
      window.umami.track(name, payload);
      return;
    }
    if (attempt < UMAMI_WAIT_ATTEMPTS) {
      window.setTimeout(() => sendToUmami(name, payload, attempt + 1), UMAMI_WAIT_MS);
    }
  }

  function track(name, input = {}) {
    if (!ALLOWED_EVENTS.has(name)) return false;
    const payload = buildPayload(input);
    if (isDuplicate(name, payload)) return false;
    sendToUmami(name, payload);
    return true;
  }

  function itemPayload(item, quantity = item?.quantity) {
    const cents = toNumber(item?.final_price ?? item?.price);
    return {
      product_handle: sanitizeToken(item?.product_handle || item?.handle),
      variant_id: String(item?.variant_id || item?.id || ''),
      quantity: toNumber(quantity),
      value: cents ? (cents * toNumber(quantity)) / 100 : undefined,
      currency: cartCurrency || config.currency,
    };
  }

  function snapshotFromCart(cart) {
    const next = new Map();
    if (!cart || !Array.isArray(cart.items)) return next;
    cartCurrency = sanitizeToken(cart.currency, 12) || cartCurrency;
    cartTotal = toNumber(cart.total_price) / 100;

    for (const item of cart.items) {
      const variantId = String(item.variant_id || item.id || '');
      if (!variantId) continue;
      const existing = next.get(variantId);
      const quantity = toNumber(item.quantity);
      next.set(variantId, {
        ...item,
        quantity: quantity + toNumber(existing?.quantity),
      });
    }
    return next;
  }

  function applyCart(cart, source = '') {
    const next = snapshotFromCart(cart);
    if (cartSnapshot.size) {
      for (const [variantId, oldItem] of cartSnapshot) {
        const nextQuantity = toNumber(next.get(variantId)?.quantity);
        const removed = Math.max(0, toNumber(oldItem.quantity) - nextQuantity);
        if (removed) {
          track('remove-from-cart', itemPayload(oldItem, removed));
        }
      }
    }
    cartSnapshot = next;

    if (source === 'cart-view') {
      track('cart-view', {
        value: cartTotal,
        currency: cartCurrency,
      });
    }
  }

  async function refreshCart(source = '') {
    try {
      const response = await nativeFetch('/cart.js', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) return;
      applyCart(await response.json(), source);
    } catch {
      // Analytics must never interfere with cart operations.
    }
  }

  function requestUrl(input) {
    try {
      if (typeof input === 'string') return new URL(input, window.location.origin);
      if (input instanceof URL) return input;
      if (input && typeof input.url === 'string') return new URL(input.url, window.location.origin);
    } catch {
      return null;
    }
    return null;
  }

  function bodyToFormData(body) {
    if (body instanceof FormData) return body;
    if (body instanceof URLSearchParams) return body;
    if (typeof body === 'string') return new URLSearchParams(body);
    return null;
  }

  function addRequestPayload(body) {
    const data = bodyToFormData(body);
    if (!data) return {};
    return {
      variant_id: String(data.get('id') || ''),
      quantity: toNumber(data.get('quantity')) || 1,
    };
  }

  async function handleNetworkResponse(url, body, response) {
    if (!url || !response?.ok || url.origin !== window.location.origin) return;
    const path = url.pathname.replace(/\/+$/, '');

    try {
      if (path.endsWith('/cart/add') || path.endsWith('/cart/add.js')) {
        const result = await response.clone().json();
        const requestPayload = addRequestPayload(body);
        const items = Array.isArray(result?.items) ? result.items : [result];
        for (const item of items) {
          track('add-to-cart', itemPayload(item, item?.quantity || requestPayload.quantity));
        }
        window.setTimeout(() => refreshCart(), 0);
      } else if (
        path.endsWith('/cart/change') ||
        path.endsWith('/cart/change.js') ||
        path.endsWith('/cart/update') ||
        path.endsWith('/cart/update.js')
      ) {
        applyCart(await response.clone().json());
      }
    } catch {
      if (path.includes('/cart/')) window.setTimeout(() => refreshCart(), 0);
    }
  }

  const nativeFetch = window.fetch.bind(window);
  window.fetch = function igsFetch(input, init = {}) {
    const url = requestUrl(input);
    const body = init.body;
    return nativeFetch(input, init).then((response) => {
      void handleNetworkResponse(url, body, response);
      return response;
    });
  };

  const NativeXHR = window.XMLHttpRequest;
  if (NativeXHR?.prototype) {
    const nativeOpen = NativeXHR.prototype.open;
    const nativeSend = NativeXHR.prototype.send;

    NativeXHR.prototype.open = function igsOpen(method, url, ...rest) {
      this.__igsUrl = requestUrl(url);
      return nativeOpen.call(this, method, url, ...rest);
    };

    NativeXHR.prototype.send = function igsSend(body) {
      this.__igsBody = body;
      this.addEventListener('loadend', () => {
        const url = this.__igsUrl;
        if (!url || this.status < 200 || this.status >= 300) return;
        const responseBody = this.status === 204 || this.status === 205 ? null : this.responseText;
        const response = new Response(responseBody, {
          status: this.status,
          headers: { 'Content-Type': this.getResponseHeader('Content-Type') || 'application/json' },
        });
        void handleNetworkResponse(url, this.__igsBody, response);
      }, { once: true });
      return nativeSend.call(this, body);
    };
  }

  function productHandleFromElement(element) {
    if (config.productHandle) return config.productHandle;
    const scope = element?.closest?.('product-card, .product-card, [data-product-id]') || element;
    const link = scope?.querySelector?.('a[href*="/products/"]');
    const match = link?.getAttribute('href')?.match(/\/products\/([^/?#]+)/);
    return sanitizeToken(match?.[1]);
  }

  function onSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const action = form.getAttribute('action') || '';
    const isAddToCart =
      action.includes('/cart/add') ||
      form.dataset.type === 'add-to-cart-form' ||
      form.classList.contains('shopify-product-form');
    if (!isAddToCart) return;
    if (form.closest('product-form-component')) return;

    const data = new FormData(form);
    track('add-to-cart', {
      product_handle: productHandleFromElement(form),
      variant_id: String(data.get('id') || ''),
      quantity: toNumber(data.get('quantity')) || 1,
      value: config.valueCents ? config.valueCents / 100 : undefined,
      currency: config.currency,
    });
  }

  function isCheckoutElement(element) {
    return Boolean(element?.closest?.(
      '[name="checkout"], #checkout, a[href*="/checkout"], .shopify-payment-button__button, shopify-accelerated-checkout-cart'
    ));
  }

  function isWhatsAppLink(element) {
    const link = element?.closest?.('a[href]');
    if (!link) return false;
    try {
      const hostname = new URL(link.href, window.location.origin).hostname.toLowerCase();
      return hostname === 'wa.me' || hostname === 'api.whatsapp.com' || hostname === 'web.whatsapp.com';
    } catch {
      return false;
    }
  }

  function onClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (isCheckoutElement(target)) {
      track('begin-checkout', { value: cartTotal, currency: cartCurrency || config.currency });
    }
    if (isWhatsAppLink(target)) {
      track('whatsapp-click', {});
    }
  }

  function onDocumentSubmit(event) {
    onSubmit(event);
    if (isCheckoutElement(event.submitter || event.target)) {
      track('begin-checkout', { value: cartTotal, currency: cartCurrency || config.currency });
    }
  }

  function observeCartDrawer() {
    const observer = new MutationObserver((records) => {
      for (const record of records) {
        if (record.type !== 'attributes' || record.attributeName !== 'open') continue;
        const dialog = record.target;
        if (dialog instanceof HTMLDialogElement && dialog.open && dialog.closest('cart-drawer-component')) {
          void refreshCart('cart-view');
        }
      }
    });

    document.querySelectorAll('cart-drawer-component dialog').forEach((dialog) => {
      observer.observe(dialog, { attributes: true, attributeFilter: ['open'] });
    });
  }

  function emitAttribution() {
    if (!attribution.influencer_code && !attribution.campaign_id) return;
    const signature = `${attribution.influencer_code || ''}|${attribution.campaign_id || ''}`;
    if (sessionStore?.getItem(ATTRIBUTION_EVENT_KEY) === signature) return;

    if (track('influencer-attributed', {
      influencer_code: attribution.influencer_code,
      campaign_id: attribution.campaign_id,
    })) {
      try {
        sessionStore?.setItem(ATTRIBUTION_EVENT_KEY, signature);
      } catch {
        // Non-critical storage failure.
      }
    }
  }

  function init() {
    document.addEventListener('click', onClick, true);
    document.addEventListener('submit', onDocumentSubmit, true);
    observeCartDrawer();
    emitAttribution();

    void refreshCart(window.location.pathname === '/cart' ? 'cart-view' : '');

    if (config.pageType === 'product' && config.productHandle) {
      track('product-view', {
        product_handle: config.productHandle,
        variant_id: config.variantId,
        value: config.valueCents ? config.valueCents / 100 : undefined,
        currency: config.currency,
      });
    }
  }

  window.__IGS_ANALYTICS__ = Object.freeze({
    track,
    buildPayload,
    getAttribution: () => ({ ...attribution }),
    refreshCart,
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
